import { db } from '@/lib/firebase';
import { collection, getDocs, query, where, getCountFromServer, orderBy, limit } from 'firebase/firestore';
import { ReferralStatus, RewardStatus, type UserStats, type RecruiterStats, type AdminStats, type LeaderboardUser } from '@/lib/schema';
import { type RewardData } from '@/components/referrals/tableConfigs';
import { getUserByUid } from './users';

export const getUserStats = async (userId: string): Promise<UserStats> => {
  const referralsQuery = query(collection(db, 'referrals'), where('referrerId', '==', userId));
  const totalReferrals = (await getCountFromServer(referralsQuery)).data().count;
  
  const successfulQuery = query(referralsQuery, where('status', '==', ReferralStatus.HIRED));
  const successfulHires = (await getCountFromServer(successfulQuery)).data().count;
  
  const pendingQuery = query(referralsQuery, where('status', 'in', [ReferralStatus.SUBMITTED, ReferralStatus.UNDER_REVIEW]));
  const pendingReferrals = (await getCountFromServer(pendingQuery)).data().count;
  
  // Calculate total earnings from rewards collection
  const rewardsQuery = query(
    collection(db, 'rewards'),
    where('referrerId', '==', userId)
  );
  const rewardsSnapshot = await getDocs(rewardsQuery);
  const totalEarnings = rewardsSnapshot.docs.reduce((sum, doc) => {
    const data = doc.data();
    const amount = data.amount ? parseInt(data.amount.toString().replace(/[$,]/g, '')) : 0;
    return sum + amount;
  }, 0);
  
  return { totalReferrals, successfulHires, pendingReferrals, totalEarnings };
};

export const getRecruiterStats = async (recruiterId: string): Promise<RecruiterStats> => {
  const jobsQuery = query(collection(db, 'jobs'), where('recruiterId', '==', recruiterId));
  const activeJobsQuery = query(jobsQuery, where('status', '==', 'active'));
  const activeJobs = (await getCountFromServer(activeJobsQuery)).data().count;
  
  // Total referrals for recruiter's jobs
  const jobSnapshot = await getDocs(jobsQuery);
  const jobIds = jobSnapshot.docs.map(d => d.id);
  const referralsQuery = query(collection(db, 'referrals'), where('jobId', 'in', jobIds));
  const totalReferrals = (await getCountFromServer(referralsQuery)).data().count;
  
  const successfulQuery = query(referralsQuery, where('status', '==', ReferralStatus.HIRED));
  const successfulHires = (await getCountFromServer(successfulQuery)).data().count;
  
  const conversionRate = totalReferrals > 0 ? (successfulHires / totalReferrals) * 100 : 0;
  
  return { activeJobs, totalReferrals, successfulHires, conversionRate };
};

export const getAdminStats = async (): Promise<AdminStats> => {
  const totalUsers = (await getCountFromServer(collection(db, 'users'))).data().count;
  const activeCandidates = (await getCountFromServer(collection(db, 'candidates'))).data().count;
  const totalJobs = (await getCountFromServer(collection(db, 'jobs'))).data().count;
  const totalReferrals = (await getCountFromServer(collection(db, 'referrals'))).data().count;
  
  // Calculate pending rewards amount
  const pendingRewardsQuery = query(
    collection(db, 'referrals'), 
    where('rewardStatus', '==', RewardStatus.PENDING)
  );
  const pendingRewardsSnapshot = await getDocs(pendingRewardsQuery);
  const pendingRewards = pendingRewardsSnapshot.docs.reduce((sum, doc) => {
    const data = doc.data();
    return sum + (data.rewardAmount || 0);
  }, 0);
  
  // Calculate paid rewards amount
  const paidRewardsQuery = query(
    collection(db, 'referrals'), 
    where('rewardStatus', '==', RewardStatus.PAID)
  );
  const paidRewardsSnapshot = await getDocs(paidRewardsQuery);
  const paidRewards = paidRewardsSnapshot.docs.reduce((sum, doc) => {
    const data = doc.data();
    return sum + (data.rewardAmount || 0);
  }, 0);
  
  // Calculate total rewards accumulated (pending + eligible + paid)
  const allRewardsQuery = query(
    collection(db, 'referrals'), 
    where('rewardStatus', 'in', [RewardStatus.PENDING, RewardStatus.ELIGIBLE, RewardStatus.PAID])
  );
  const allRewardsSnapshot = await getDocs(allRewardsQuery);
  const totalRewardsAccumulated = allRewardsSnapshot.docs.reduce((sum, doc) => {
    const data = doc.data();
    return sum + (data.rewardAmount || 0);
  }, 0);
  
  return { 
    totalUsers, 
    activeCandidates, 
    totalJobs, 
    totalReferrals, 
    pendingRewards, 
    paidRewards, 
    totalRewardsAccumulated 
  };
};

export const getLeaderboard = async (limitCount: number = 10): Promise<LeaderboardUser[]> => {
  try {
    // Get leaderboard data directly from leaderboard collection
    const leaderboardQuery = query(
      collection(db, 'leaderboard'),
      orderBy('totalEarnings', 'desc'), // Sort by totalEarnings descending
      limit(limitCount)
    );
    
    const leaderboardSnapshot = await getDocs(leaderboardQuery);
    
    const leaderboardData: LeaderboardUser[] = leaderboardSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: data.userId || doc.id,
        name: data.username || 'Unknown User',
        username: data.username || 'Unknown User',
        firstName: '',
        lastName: '',
        email: '',
        totalReferrals: data.referrals || 0,
        successfulHires: data.hired || 0,
        totalEarnings: data.totalEarnings || 0,
        avatar: ''
      };
    });
    
    return leaderboardData;
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    return [];
  }
};

// Get user rewards for the rewards page
export const getUserRewards = async (userId: string): Promise<RewardData[]> => {
  try {
    // Query rewards collection directly for this user
    const rewardsQuery = query(
      collection(db, 'rewards'),
      where('referrerId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(rewardsQuery);
    
    // Transform reward documents into RewardData format
    const rewardsData = snapshot.docs.map((docSnap) => {
      try {
        const data = docSnap.data();
        
        const rewardData: RewardData = {
          id: docSnap.id,
          candidateName: data.candidateName || 'Unknown Candidate',
          jobTitle: data.jobTitle || 'Unknown Job',
          amount: data.amount || '0',
          status: data.status as RewardStatus,
          date: data.date || (data.createdAt?.toDate ? data.createdAt.toDate().toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }) : 'Unknown Date'),
          avatar: data.avatar || data.candidateAvatar
        };
        
        return rewardData;
      } catch (error) {
        console.error('Error processing reward data:', error);
        return null;
      }
    });
    
    // Filter out null values and return
    return rewardsData.filter((reward): reward is RewardData => reward !== null);
  } catch (error) {
    console.error('Error fetching user rewards:', error);
    return [];
  }
};

// Get all rewards for admin view
export const getAllRewards = async (): Promise<RewardData[]> => {
  try {
    // Query all rewards ordered by creation date
    const rewardsQuery = query(
      collection(db, 'rewards'),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(rewardsQuery);
    
    // Transform reward documents into RewardData format with referrer lookup
    const rewardsData = await Promise.all(
      snapshot.docs.map(async (docSnap) => {
        try {
          const data = docSnap.data();
          
          // Fetch referrer information if referrerId exists
          let referrerName = 'Unknown Referrer';
          if (data.referrerId) {
            try {
              const referrer = await getUserByUid(data.referrerId);
              if (referrer) {
                referrerName = `${referrer.firstName} ${referrer.lastName}`;
              }
            } catch (error) {
              console.error('Error fetching referrer:', error);
              // Keep default 'Unknown Referrer'
            }
          }
          
          const rewardData: RewardData = {
            id: docSnap.id,
            candidateName: data.candidateName || 'Unknown Candidate',
            jobTitle: data.jobTitle || 'Unknown Job',
            amount: data.amount || '0',
            status: data.status as RewardStatus,
            date: data.date || (data.createdAt?.toDate ? data.createdAt.toDate().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            }) : 'Unknown Date'),
            avatar: data.avatar || data.candidateAvatar,
            referrerName,
            referrerId: data.referrerId || ''
          };
          
          return rewardData;
        } catch (error) {
          console.error('Error processing reward data:', error);
          return null;
        }
      })
    );
    
    // Filter out null values and return
    return rewardsData.filter((reward): reward is RewardData => reward !== null);
  } catch (error) {
    console.error('Error fetching all rewards:', error);
    return [];
  }
};

// Get admin stats filtered by recruiter's jobs
export const getRecruiterAdminStats = async (recruiterId: string): Promise<AdminStats> => {
  // Get all jobs for this recruiter
  const jobsQuery = query(collection(db, 'jobs'), where('recruiterId', '==', recruiterId));
  const jobSnapshot = await getDocs(jobsQuery);
  const jobIds = jobSnapshot.docs.map(d => d.id);
  
  // If no jobs, return zeros
  if (jobIds.length === 0) {
    return {
      totalUsers: 0,
      activeCandidates: 0,
      totalJobs: 0,
      totalReferrals: 0,
      pendingRewards: 0,
      paidRewards: 0,
      totalRewardsAccumulated: 0
    };
  }
  
  // Get referrals for recruiter's jobs
  const referralsQuery = query(collection(db, 'referrals'), where('jobId', 'in', jobIds));
  const referralsSnapshot = await getDocs(referralsQuery);
  const totalReferrals = referralsSnapshot.size;
  
  // Get unique candidates from these referrals
  const candidateIds = new Set(referralsSnapshot.docs.map(doc => doc.data().candidateId));
  const activeCandidates = candidateIds.size;
  
  // Count users who have referred to these jobs (unique referrers)
  const referrerIds = new Set(referralsSnapshot.docs.map(doc => doc.data().referrerId));
  const totalUsers = referrerIds.size;
  
  return {
    totalUsers,
    activeCandidates,
    totalJobs: jobIds.length,
    totalReferrals,
    pendingRewards: 0, // Recruiters don't need rewards stats
    paidRewards: 0,
    totalRewardsAccumulated: 0
  };
};

